import { IAction } from '../actions/types'
import TimeLine from '../modules/timeline/Module'
import { IQuestionModel, IQuestionModelState, QuestionModelTypes, QuestionType, QuestionViewFrame } from './types'

export default function BaseQuestionModel<
  T extends QuestionModelTypes = QuestionModelTypes,
  QuestionModelState extends IQuestionModelState<T> = IQuestionModelState<T>
>({
  questionType,
  questionSchema,
  action,
  submitResponse,
  checkResponse,
  initState = state => state,
  validateResponse,
  namedFrames,
  collectPreloadImages
}: {
  questionType: QuestionType
  questionSchema: T['schema']
  action: IAction
  submitResponse: (state: QuestionModelState) => T['submitResponse']
  checkResponse?: (state: QuestionModelState) => T['checkResponse']
  initState?: (state: QuestionModelState) => QuestionModelState
  validateResponse?: (state: QuestionModelState) => T['responseValidation']
  namedFrames?: QuestionViewFrame[]
  collectPreloadImages?: (state: QuestionModelState) => string[]
}): {
  model: IQuestionModel<T>
  state: QuestionModelState
} {
  const state: QuestionModelState = initState({
    questionSchema,
    action,
    response: null,
    responseValidation: { valid: false },
    frame: namedFrames?.length ? namedFrames[0] : 0,
    timeline: TimeLine.createTimeline(String(questionSchema.id)),
    screenNumber: 1,
    navigationFrame: null
  } as QuestionModelState)

  if (validateResponse) {
    state.responseValidation = validateResponse(state)
  }

  const model: IQuestionModel<T> = {
    get id() {
      return state.questionSchema.id
    },
    get type() {
      return questionType
    },
    get schema() {
      return state.questionSchema
    },
    get action() {
      return state.action
    },
    get response() {
      return state.response
    },
    set response(value) {
      state.response = value
      if (validateResponse) {
        state.responseValidation = validateResponse(state)
      }
    },
    get responseValidation() {
      return state.responseValidation
    },
    get checkResponse() {
      return 'function' === typeof checkResponse ? checkResponse(state) : null
    },
    get submitResponse() {
      return submitResponse(state)
    },
    get frame() {
      return state.frame
    },
    set frame(f) {
      state.frame = f
    },
    nextFrame() {
      if (namedFrames) {
        const index = namedFrames.indexOf(state.frame)
        if (index < namedFrames.length - 1) {
          state.frame = namedFrames[index + 1]
          if (validateResponse) {
            state.responseValidation = validateResponse(state)
          }
          return true
        } else {
          return false
        }
      } else {
        ;(state.frame as number)++
        if (validateResponse) {
          state.responseValidation = validateResponse(state)
        }
        if (isNaN(state.frame as number)) {
          state.frame = 0
        }
        return true
      }
    },
    prevFrame() {
      if (namedFrames) {
        const index = namedFrames.indexOf(state.frame)
        if (0 < index) {
          state.frame = namedFrames[index - 1]
          return true
        } else {
          return false
        }
      } else {
        if (0 < (state.frame as number)) {
          ;(state.frame as number)--
          if (isNaN(state.frame as number)) {
            state.frame = 0
          }
          if (validateResponse) {
            state.responseValidation = validateResponse(state)
          }
          return true
        } else {
          return false
        }
      }
    },
    get timeline() {
      return state.timeline
    },
    get preloadImages() {
      return collectPreloadImages ? collectPreloadImages(state) : []
    },
    get questionTime() {
      return state.questionSchema?.time || 0
    },
    get screenNumber() {
      return state.screenNumber
    },
    set screenNumber(screenNumber) {
      state.screenNumber = screenNumber
    },
    nextScreenNumber() {
      state.screenNumber += 1
      return state.screenNumber
    },
    prevScreenNumber() {
      state.screenNumber = Math.max(1, state.screenNumber - 1)
      return state.screenNumber
    },
    get navigationFrame() {
      return state.navigationFrame
    },
    set navigationFrame(navigationFrame) {
      state.navigationFrame = navigationFrame
    }
  }
  return { model, state }
}
